import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaQrcode, FaCheck, FaTimes, FaInfoCircle, FaCoins, FaShieldAlt, FaCalendarAlt, FaFileAlt } from 'react-icons/fa';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { theme } from '../styles/GlobalStyles';

const scanAnim = keyframes`
  0%   { transform: translateY(-90px); }
  50%  { transform: translateY(90px); }
  100% { transform: translateY(-90px); }
`;

const DemoContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const DemoSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const StepCard = styled.div`
  background: ${props => props.active ? 'rgba(97,93,255,0.1)' : theme.cardBg};
  border: 1px solid ${props => props.active ? 'rgba(97,93,255,0.45)' : theme.border};
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  backdrop-filter: blur(12px);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 0 24px rgba(97,93,255,0.2)' : theme.cardShadow};

  &:hover {
    border-color: rgba(97,93,255,0.35);
    background: ${props => props.active ? 'rgba(97,93,255,0.12)' : theme.cardBgHover};
    transform: ${props => !props.active && 'translateY(-4px)'};
  }
`;

const StepIconCircle = styled.div`
  width: 64px;
  height: 64px;
  background: ${props => props.active
    ? theme.gradient
    : 'linear-gradient(135deg, rgba(97,93,255,0.15), rgba(61,217,235,0.08))'
  };
  border: 1px solid ${props => props.active ? 'transparent' : 'rgba(97,93,255,0.2)'};
  border-radius: 50%;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: ${props => props.active ? 'white' : '#a5a2ff'};
  box-shadow: ${props => props.active ? '0 0 20px rgba(97,93,255,0.4)' : 'none'};
`;

const StepTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 0.4rem;
  color: ${theme.text};
`;

const StepDescription = styled.p`
  color: ${theme.textMuted};
  font-size: 0.82rem;
  line-height: 1.55;
  margin: 0;
`;

const DemoContent = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 20px;
  overflow: hidden;
  backdrop-filter: blur(14px);
  box-shadow: ${theme.cardShadow};
  margin-bottom: 4rem;
`;

const PhoneFrame = styled.div`
  max-width: 320px;
  margin: 2.5rem auto 2rem;
`;

const Phone = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 200%;
  background: #0a0a14;
  border-radius: 36px;
  border: 2px solid rgba(97,93,255,0.3);
  box-shadow: 0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(97,93,255,0.1);
  overflow: hidden;
`;

const PhoneScreen = styled.div`
  position: absolute;
  top: 2%; left: 5%; right: 5%; bottom: 2%;
  background: #0f1020;
  border-radius: 30px;
  overflow: hidden;
  transition: all 0.5s ease;
`;

/* ── Scanner screen ── */
const ScannerScreen = styled.div`
  background: #0c0d1c;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ScannerHeader = styled.div`
  background: ${theme.gradient};
  color: white;
  padding: 1.25rem 1rem;
  text-align: center;

  h3 { margin-bottom: 0.3rem; font-size: 1.1rem; }
  p  { font-size: 0.8rem; opacity: 0.9; margin: 0; }
`;

const ScannerViewfinder = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    width: 180px; height: 180px;
    border: 2px solid rgba(97,93,255,0.7);
    border-radius: 10px;
  }

  &::before {
    content: "";
    position: absolute;
    width: 210px; height: 2px;
    background: linear-gradient(to right, transparent, #615DFF, #3DD9EB, transparent);
    animation: ${scanAnim} 2s ease-in-out infinite;
  }
`;

const QRCode = styled.div`
  width: 160px; height: 160px;
  background-image: url('https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=https://prodseal.com/verify/PS1234567890&bgcolor=0f1020&color=a5a2ff');
  background-size: cover;
  background-position: center;
  border-radius: 6px;
`;

/* ── Verification screen ── */
const VerificationScreen = styled.div`
  background: #0c0d1c;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const VerificationHeader = styled.div`
  background: ${props => props.verified ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #b91c1c, #ef4444)'};
  color: white;
  padding: 1.5rem 1rem;
  text-align: center;

  .status-icon { font-size: 2.5rem; margin-bottom: 0.75rem; }
  h2 { margin-bottom: 0.3rem; font-size: 1.25rem; }
  p  { opacity: 0.9; margin: 0; font-size: 0.82rem; }
`;

const ProductDetails = styled.div`
  padding: 1rem;
`;

const ProductCard = styled.div`
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProductImage = styled.div`
  height: 100px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
`;

const ProductInfo = styled.div`
  padding: 0.75rem 1rem;
  background: rgba(255,255,255,0.03);

  h3 { font-size: 0.9rem; margin-bottom: 0.4rem; color: ${theme.text}; }
  p  { font-size: 0.75rem; color: ${theme.textMuted}; margin-bottom: 0.2rem; }
`;

const RewardsInfo = styled.div`
  background: rgba(97,93,255,0.1);
  border: 1px solid rgba(97,93,255,0.2);
  padding: 0.75rem 1rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .reward-icon {
    font-size: 1.4rem;
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  div {
    flex: 1;
    h4 { font-size: 0.85rem; color: ${theme.text}; margin-bottom: 0.15rem; }
    p  { font-size: 0.75rem; color: ${theme.textMuted}; margin: 0; }
  }
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;

  button {
    padding: 0.6rem 0.5rem;
    font-size: 0.78rem;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;

    svg { font-size: 1rem; }

    &:first-child {
      background: ${theme.gradient};
      color: white;
      &:hover { opacity: 0.9; }
    }

    &:last-child {
      background: rgba(97,93,255,0.12);
      border: 1px solid rgba(97,93,255,0.25);
      color: #a5a2ff;
      &:hover { background: rgba(97,93,255,0.2); }
    }
  }
`;

const CounterfeitWarning = styled.div`
  background: rgba(185,28,28,0.12);
  border: 1px solid rgba(239,68,68,0.3);
  padding: 0.75rem 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;

  h3 {
    color: #fca5a5;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 0.4rem;
    font-size: 0.88rem;
  }

  p { font-size: 0.78rem; color: rgba(252,165,165,0.8); margin: 0; line-height: 1.5; }
`;

const NextStepsList = styled.ul`
  padding-left: 1.25rem;
  margin-bottom: 1rem;

  li {
    margin-bottom: 0.4rem;
    font-size: 0.78rem;
    color: ${theme.textMuted};
  }
`;

const WarrantySection = styled.div`
  background: rgba(97,93,255,0.06);
  border: 1px solid rgba(97,93,255,0.15);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  margin-top: 1rem;

  h4 { margin-bottom: 0.6rem; font-size: 0.85rem; color: ${theme.text}; }

  ul {
    padding-left: 1rem;
    margin: 0;

    li { margin-bottom: 0.35rem; font-size: 0.78rem; color: ${theme.textMuted}; }
  }
`;

/* ── Demo controls ── */
const DemoControls = styled.div`
  text-align: center;
  padding: 2rem 2rem 2.5rem;

  h3 { margin-bottom: 1.5rem; color: ${theme.text}; font-size: 1.1rem; }
`;

const ControlButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CtaText = styled.p`
  max-width: 600px;
  margin: 0 auto 2rem;
  color: ${theme.textMuted};
  font-size: 1rem;
  line-height: 1.7;
`;

const QrDemoPage = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [productVerified, setProductVerified] = useState(true);

  const handleNextStep = () => setActiveStep(s => s < 3 ? s + 1 : 1);

  const renderScreenContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <ScannerScreen>
            <ScannerHeader>
              <h3>Scan Product QR Code</h3>
              <p>Position QR code within the frame to verify</p>
            </ScannerHeader>
            <ScannerViewfinder>
              <QRCode />
            </ScannerViewfinder>
          </ScannerScreen>
        );

      case 2:
        return productVerified ? (
          <VerificationScreen>
            <VerificationHeader verified>
              <div className="status-icon"><FaCheck /></div>
              <h2>Product Verified</h2>
              <p>This is an authentic product</p>
            </VerificationHeader>
            <ProductDetails>
              <ProductCard>
                <ProductImage src="https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80" />
                <ProductInfo>
                  <h3>Premium Headphones X1</h3>
                  <p><strong>Brand:</strong> AudioMax</p>
                  <p><strong>Serial:</strong> PS-9847-23A</p>
                  <p><strong>Manufactured:</strong> June 2023</p>
                </ProductInfo>
              </ProductCard>
              <RewardsInfo>
                <div className="reward-icon"><FaCoins /></div>
                <div>
                  <h4>+25 Reward Points Earned!</h4>
                  <p>Your new balance: 1,325 points</p>
                </div>
              </RewardsInfo>
              <ActionButtons>
                <button><FaShieldAlt />Activate Warranty</button>
                <button><FaFileAlt />Product Details</button>
              </ActionButtons>
            </ProductDetails>
          </VerificationScreen>
        ) : (
          <VerificationScreen>
            <VerificationHeader verified={false}>
              <div className="status-icon"><FaTimes /></div>
              <h2>Warning: Potential Counterfeit</h2>
              <p>This product may not be authentic</p>
            </VerificationHeader>
            <ProductDetails>
              <CounterfeitWarning>
                <h3><FaInfoCircle /> This product could not be verified</h3>
                <p>This QR code is not recognised in our system or has been tampered with. This may indicate a counterfeit product.</p>
              </CounterfeitWarning>
              <h4 style={{ marginBottom: '0.6rem', fontSize: '0.88rem', color: theme.text }}>What to do next:</h4>
              <NextStepsList>
                <li>Contact the seller and request proof of authenticity</li>
                <li>Report this incident to the brand</li>
                <li>Return the product if possible</li>
              </NextStepsList>
              <ActionButtons>
                <button>Report Counterfeit</button>
                <button>Contact Support</button>
              </ActionButtons>
            </ProductDetails>
          </VerificationScreen>
        );

      case 3:
        return (
          <VerificationScreen>
            <VerificationHeader verified style={{ background: theme.gradient }}>
              <div className="status-icon"><FaCalendarAlt /></div>
              <h2>Warranty Activated</h2>
              <p>Valid until July 15, 2026</p>
            </VerificationHeader>
            <ProductDetails>
              <ProductCard>
                <ProductImage src="https://images.unsplash.com/photo-1505740420928-5e560c06d30e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80" />
                <ProductInfo>
                  <h3>Premium Headphones X1</h3>
                  <p><strong>Brand:</strong> AudioMax</p>
                  <p><strong>Serial:</strong> PS-9847-23A</p>
                </ProductInfo>
              </ProductCard>
              <WarrantySection>
                <h4>Warranty Details:</h4>
                <ul>
                  <li>2 Year Limited Warranty</li>
                  <li>Covers manufacturing defects</li>
                  <li>Free repairs for first 12 months</li>
                </ul>
              </WarrantySection>
              <ActionButtons>
                <button>Save Warranty</button>
                <button>Support Options</button>
              </ActionButtons>
            </ProductDetails>
          </VerificationScreen>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <PageBanner
        title="Interactive ProdSeal Demo"
        subtitle="Experience the product authentication and rewards journey firsthand."
      />

      <ContentSection>
        <DemoContainer>
          <DemoSteps>
            {[
              { step: 1, icon: <FaQrcode />, title: 'Scan QR Code', desc: 'Consumers scan the unique ProdSeal QR code on your product.' },
              { step: 2, icon: <FaShieldAlt />, title: 'Verify Product', desc: 'Get immediate verification results and earn reward points.' },
              { step: 3, icon: <FaCalendarAlt />, title: 'Activate Warranty', desc: 'Easily register product warranty in the same seamless flow.' },
            ].map(({ step, icon, title, desc }) => (
              <StepCard key={step} active={activeStep === step} onClick={() => setActiveStep(step)}>
                <StepIconCircle active={activeStep === step}>{icon}</StepIconCircle>
                <StepTitle>{title}</StepTitle>
                <StepDescription>{desc}</StepDescription>
              </StepCard>
            ))}
          </DemoSteps>

          <DemoContent>
            <PhoneFrame>
              <Phone>
                <PhoneScreen>
                  {renderScreenContent()}
                </PhoneScreen>
              </Phone>
            </PhoneFrame>

            <DemoControls>
              <h3>Demo Controls</h3>
              <ControlButtons>
                <Button primary onClick={handleNextStep}>Next Step</Button>
                <Button secondary onClick={() => setProductVerified(v => !v)}>
                  Toggle {productVerified ? 'Counterfeit' : 'Authentic'} Product
                </Button>
              </ControlButtons>
            </DemoControls>
          </DemoContent>

          <div style={{ textAlign: 'center' }}>
            <SectionTitle>Ready to protect your products?</SectionTitle>
            <CtaText>
              ProdSeal provides a complete solution for product authentication, warranty activation,
              and customer engagement. Start building trust in every purchase.
            </CtaText>
            <Button primary to="/contact">Contact Us for a Demo</Button>
          </div>
        </DemoContainer>
      </ContentSection>
    </>
  );
};

export default QrDemoPage;
